import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';
import {
  CreditCardIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  ClockIcon,
  ServerIcon,
  KeyIcon,
  UserGroupIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

// Bart volume pricing bands (fixed ladder; subplan_1..5 map to these in order).
const VOLUME_BANDS = [
  { min: 10, max: 999, label: '10 – 999' },
  { min: 1000, max: 2499, label: '1,000 – 2,499' },
  { min: 2500, max: 4999, label: '2,500 – 4,999' },
  { min: 5000, max: 9999, label: '5,000 – 9,999' },
  { min: 10000, max: Infinity, label: '10,000+' },
];

const fmt = (n) => (n == null ? '0' : Number(n).toLocaleString());

const PROXY_LIST_PAGE = 200;

const BartProxies = () => {
  const { isDarkMode } = useTheme();
  const [balance, setBalance] = useState(null);
  const [config, setConfig] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [customPrices, setCustomPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Buy form state
  const [buyPlanId, setBuyPlanId] = useState('');
  const [buyValidity, setBuyValidity] = useState(720);
  const [buyCount, setBuyCount] = useState(10);
  const [buying, setBuying] = useState(false);
  const [showCustomPricing, setShowCustomPricing] = useState(false);

  // Tab + proxy pool detail state
  const [activeTab, setActiveTab] = useState('reseller');
  const [expandedToken, setExpandedToken] = useState(null);
  const [proxyDetail, setProxyDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [proxyFilter, setProxyFilter] = useState('');
  const [proxyPage, setProxyPage] = useState(0);
  const [copiedAll, setCopiedAll] = useState(false);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const [b, c, p, cp] = await Promise.all([
        api.get('/bart-proxies/balance'),
        api.get('/bart-proxies/config'),
        api.get('/bart-proxies/proxies'),
        api.get('/bart-proxies/custom-prices'),
      ]);
      setBalance(b.data?.balance ?? null);
      setConfig(c.data?.data?.plans || []);
      setProxies(p.data?.data || []);
      setCustomPrices(cp.data?.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load Bart Proxies');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doAction = async (fn, successMsg) => {
    try {
      await fn();
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Action failed');
    }
  };

  const handleBuy = async (e) => {
    e.preventDefault();
    if (!buyPlanId) return toast.error('Pick a plan');
    setBuying(true);
    try {
      await api.post('/bart-proxies/create', {
        planId: buyPlanId, validityHours: buyValidity, numberOfProxies: buyCount,
      });
      toast.success('Proxies purchased');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Purchase failed');
    } finally {
      setBuying(false);
    }
  };

  const handleRenew = (authToken) =>
    doAction(() => api.post('/bart-proxies/add-validity', { authToken, validityHours: 720 }), 'Renewed +1 month');

  const handleDelete = (authToken) => {
    if (!window.confirm(`Delete proxy group ${authToken}? This cannot be undone.`)) return;
    doAction(() => api.post('/bart-proxies/delete', { authToken }), 'Deleted');
  };

  const handleSetLimit = async (authToken, maxAccounts) => {
    try {
      await api.post('/bart-proxies/limit', { authToken, maxAccounts: Number(maxAccounts) });
      toast.success('Limit updated');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to set limit');
    }
  };

  const handleSetCustomPrice = async (planId, value) => {
    try {
      await api.post('/bart-proxies/custom-prices', { planId, customPrice: value === '' ? null : Number(value) });
      toast.success('Custom price saved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save custom price');
    }
  };

  const toggleProxyDetail = async (authToken) => {
    if (expandedToken === authToken) {
      setExpandedToken(null);
      return;
    }
    setExpandedToken(authToken);
    setProxyDetail(null);
    setProxyFilter('');
    setProxyPage(0);
    setDetailLoading(true);
    try {
      const r = await api.get(`/bart-proxies/proxies/${encodeURIComponent(authToken)}`);
      setProxyDetail(r.data?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load proxy list');
      setExpandedToken(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const copyAllProxies = () => {
    const list = proxyDetail?.proxies || [];
    if (!list.length) return;
    const f = proxyFilter.trim().toLowerCase();
    const out = f ? list.filter((p) => String(p).toLowerCase().includes(f)) : list;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(out.join('\n')).catch(() => {});
    }
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 1500);
  };

  // Group config by planId, expanding the 5 volume tiers (subplan_1..5) per plan.
  const planGroups = useMemo(() => {
    const groups = {};
    config.forEach((p) => {
      const g = groups[p.planId] || (groups[p.planId] = { planId: p.planId, servername: p.servername, tiers: [] });
      const m = String(p.subPlanId || '').match(/(\d+)$/);
      const idx = m ? Math.max(0, parseInt(m[1], 10) - 1) : 0;
      if (VOLUME_BANDS[idx]) {
        g.tiers[idx] = { ...VOLUME_BANDS[idx], price: p.price, soldOut: !!p.soldOut };
      }
    });
    return Object.values(groups)
      .filter((g) => g.tiers.length > 0)
      .sort((a, b) => a.servername.localeCompare(b.servername))
      .map((g) => ({ ...g, customPrice: customPrices[g.planId] ?? null }));
  }, [config, customPrices]);

  const selectedPlan = planGroups.find((g) => g.planId === buyPlanId) || null;

  // Auto-derive the tier from the typed quantity (band that contains the count).
  const activeTierIdx = useMemo(() => {
    if (!selectedPlan) return 0;
    let idx = 0;
    selectedPlan.tiers.forEach((t, i) => {
      if (buyCount >= t.min) idx = i;
    });
    return idx;
  }, [selectedPlan, buyCount]);

  const activeTier = selectedPlan?.tiers?.[activeTierIdx] || null;
  const hasCustom = selectedPlan?.customPrice != null;
  const unitPrice = hasCustom ? selectedPlan.customPrice : (activeTier?.price ?? 0);
  const originalPrice = selectedPlan?.tiers?.[0]?.price ?? null;
  const totalCost = unitPrice * buyCount;

  // Proxy pool detail — filtered + paginated host:port list.
  const filteredProxies = useMemo(() => {
    const list = proxyDetail?.proxies || [];
    const f = proxyFilter.trim().toLowerCase();
    return f ? list.filter((p) => String(p).toLowerCase().includes(f)) : list;
  }, [proxyDetail, proxyFilter]);

  const proxyPageCount = Math.max(1, Math.ceil(filteredProxies.length / PROXY_LIST_PAGE));
  const proxyPageList = filteredProxies.slice(proxyPage * PROXY_LIST_PAGE, (proxyPage + 1) * PROXY_LIST_PAGE);

  const selectPlan = (planId) => {
    setBuyPlanId(planId);
    const g = planGroups.find((x) => x.planId === planId);
    if (g?.tiers?.length) {
      setBuyCount(g.tiers[0].min);
    }
  };

  const selectTier = (idx) => {
    if (selectedPlan?.tiers?.[idx]) {
      setBuyCount(selectedPlan.tiers[idx].min);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto mb-2" />
        Loading Bart Proxies…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + balance */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Bart Proxies</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Reseller ISP proxies — balance, inventory, renewal</p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('reseller')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'reseller'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Reseller
        </button>
        <button
          onClick={() => setActiveTab('pool')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'pool'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Proxy Pool
        </button>
      </div>

      {activeTab === 'reseller' && (
        <>
          {/* Balance + summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 bg-white dark:bg-gray-800">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <CreditCardIcon className="h-4 w-4" /> Balance
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                ${balance != null ? balance.toLocaleString() : '—'}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 bg-white dark:bg-gray-800">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <KeyIcon className="h-4 w-4" /> Auth tokens
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">{proxies.length}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 bg-white dark:bg-gray-800">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <ServerIcon className="h-4 w-4" /> Total proxies
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                {proxies.reduce((s, p) => s + (p.proxyCount || 0), 0)}
              </div>
            </div>
          </div>

          {/* Buy new proxies */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 bg-white dark:bg-gray-800">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <PlusIcon className="h-5 w-5" /> Buy new proxies
            </h2>
            <form onSubmit={handleBuy} className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <select
                value={buyPlanId}
                onChange={(e) => selectPlan(e.target.value)}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              >
                <option value="">Select plan…</option>
                {planGroups.map((g) => (
                  <option key={g.planId} value={g.planId}>
                    {g.servername}{g.customPrice != null ? ` — $${g.customPrice}/proxy` : ` — from $${g.tiers[0]?.price}/proxy`}
                  </option>
                ))}
              </select>
              <select
                value={hasCustom ? 'custom' : activeTierIdx}
                onChange={(e) => selectTier(Number(e.target.value))}
                disabled={!selectedPlan || hasCustom}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm disabled:opacity-50"
              >
                {hasCustom ? (
                  <option value="custom">Flat custom price</option>
                ) : (
                  selectedPlan?.tiers.map((t, i) => (
                    <option key={i} value={i}>
                      {t.label} units — ${t.price}/proxy{t.soldOut ? ' (sold out)' : ''}
                    </option>
                  ))
                )}
              </select>
              <input
                type="number"
                min={10}
                value={buyCount}
                onChange={(e) => setBuyCount(Number(e.target.value))}
                placeholder="Number of proxies"
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              />
              <select
                value={buyValidity}
                onChange={(e) => setBuyValidity(Number(e.target.value))}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              >
                <option value={720}>1 month</option>
                <option value={1440}>2 months</option>
                <option value={2160}>3 months</option>
              </select>
            </form>

            {/* Live total */}
            {selectedPlan && (hasCustom || activeTier) && (
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                {hasCustom ? (
                  <div className="rounded-lg bg-gray-50 dark:bg-gray-900 px-4 py-3 border border-gray-200 dark:border-gray-700">
                    <span className="text-gray-500 dark:text-gray-400">Original: </span>
                    <span className="font-semibold text-red-700 dark:text-red-400 line-through">${originalPrice}</span>
                    <span className="text-gray-400 mx-2">→</span>
                    <span className="text-gray-500 dark:text-gray-400">Your price: </span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">${unitPrice}/proxy</span>
                  </div>
                ) : (
                  <div className="rounded-lg bg-gray-50 dark:bg-gray-900 px-4 py-3 border border-gray-200 dark:border-gray-700">
                    <span className="text-gray-500 dark:text-gray-400">Price per proxy: </span>
                    <span className="font-semibold text-gray-900 dark:text-white">${activeTier?.price}</span>
                    <span className="text-gray-400 text-xs ml-2">({activeTier?.label} band)</span>
                  </div>
                )}
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900 px-4 py-3 border border-gray-200 dark:border-gray-700">
                  <span className="text-gray-500 dark:text-gray-400">Proxies: </span>
                  <span className="font-semibold text-gray-900 dark:text-white">{fmt(buyCount)}</span>
                </div>
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 px-4 py-3 border border-blue-200 dark:border-blue-800">
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Total: </span>
                  <span className="font-bold text-blue-700 dark:text-blue-300 text-lg">${fmt(totalCost)}</span>
                  <span className="text-blue-500 dark:text-blue-400 text-xs ml-1">/ {buyValidity / 720} mo</span>
                </div>
                <button
                  type="button"
                  onClick={handleBuy}
                  disabled={buying || !buyPlanId || (!hasCustom && (activeTier?.soldOut ?? true))}
                  className="ml-auto rounded-md bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 text-sm font-medium disabled:opacity-50"
                >
                  {buying ? 'Buying…' : 'Buy'}
                </button>
              </div>
            )}
          </div>

          {/* Custom pricing (drawer — closed by default) */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <button
              type="button"
              onClick={() => setShowCustomPricing((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
            >
              <div className="flex items-center gap-3">
                <BanknotesIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">Custom pricing</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Override Bart's price per package. Original price shown struck-through; your discounted price in green.
                  </p>
                </div>
              </div>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${showCustomPricing ? 'rotate-180' : ''}`} />
            </button>
            {showCustomPricing && (
              <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {planGroups.map((g) => (
                  <div key={g.planId} className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{g.servername}</div>
                      <div className="text-xs text-red-700 dark:text-red-400 line-through">
                        from ${g.tiers[0]?.price}/proxy
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-400">$</span>
                      <input
                        key={`price-${g.planId}-${g.customPrice ?? ''}`}
                        type="number"
                        step="0.01"
                        min={0}
                        defaultValue={g.customPrice ?? ''}
                        placeholder="—"
                        onBlur={(e) => {
                          const v = e.target.value;
                          const nv = v === '' ? null : Number(v);
                          if (nv !== (g.customPrice ?? null)) handleSetCustomPrice(g.planId, v);
                        }}
                        className="w-20 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'pool' && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 font-semibold text-gray-900 dark:text-white">
            Proxy groups
          </div>
          {proxies.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No reseller ISP proxies yet. Buy your first group in the Reseller tab.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-2">Auth token</th>
                    <th className="px-4 py-2">Proxies</th>
                    <th className="px-4 py-2">Expires in</th>
                    <th className="px-4 py-2">Account limit</th>
                    <th className="px-4 py-2">Slots</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {proxies.map((p) => (
                    <React.Fragment key={p.authToken}>
                      <tr className="border-b border-gray-100 dark:border-gray-800">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleProxyDetail(p.authToken)}
                            className="flex items-center gap-1.5 text-left font-mono text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400"
                          >
                            <ChevronRightIcon className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${expandedToken === p.authToken ? 'rotate-90' : ''}`} />
                            {p.authToken}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{p.proxyCount}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                            p.remainingDays <= 7
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : p.remainingDays <= 30
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                          }`}>
                            <ClockIcon className="h-3 w-3" />
                            {p.remainingDays} days
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min={1}
                              max={100}
                              defaultValue={p.maxAccounts}
                              onBlur={(e) => {
                                const v = Number(e.target.value);
                                if (v !== p.maxAccounts) handleSetLimit(p.authToken, v);
                              }}
                              className="w-16 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                            <UserGroupIcon className="h-4 w-4 text-gray-400" />
                            {p.usage?.occupied || 0}/{p.maxAccounts}
                            {p.usage?.banned > 0 && (
                              <span className="text-xs text-gray-400">({p.usage.banned} freed)</span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => handleRenew(p.authToken)}
                            className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 mr-1"
                            title="Renew +1 month"
                          >
                            Renew
                          </button>
                          <button
                            onClick={() => handleDelete(p.authToken)}
                            className="px-2 py-1 text-xs rounded border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Delete group"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                      {expandedToken === p.authToken && (
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          <td colSpan={6} className="px-4 py-4 bg-gray-50 dark:bg-gray-900/40">
                            {detailLoading ? (
                              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-2">
                                <ArrowPathIcon className="h-4 w-4 animate-spin" /> Loading proxies…
                              </div>
                            ) : proxyDetail ? (
                              <div className="space-y-3">
                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                  <span className="inline-flex items-center rounded-full px-2 py-0.5 font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                    {proxyDetail.authType === 'ip'
                                      ? 'IP auth'
                                      : proxyDetail.authType === 'user'
                                      ? 'User:pass auth'
                                      : (proxyDetail.authType || '—')}
                                  </span>
                                  {proxyDetail.authType === 'ip' && Array.isArray(proxyDetail.srcIps) && proxyDetail.srcIps.length > 0 && (
                                    <span>srcIps: {proxyDetail.srcIps.join(', ')}</span>
                                  )}
                                  {proxyDetail.authType === 'user' && proxyDetail.username && proxyDetail.username !== 'null' && (
                                    <span>username: {proxyDetail.username}</span>
                                  )}
                                  <span>{proxyDetail.proxyCount} proxies</span>
                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="relative flex-1 min-w-[200px] max-w-xs">
                                    <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                      value={proxyFilter}
                                      onChange={(e) => { setProxyFilter(e.target.value); setProxyPage(0); }}
                                      placeholder="Filter host:port…"
                                      className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
                                    />
                                  </div>
                                  <button
                                    onClick={copyAllProxies}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    {copiedAll ? <CheckIcon className="h-3.5 w-3.5 text-emerald-500" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
                                    {copiedAll ? 'Copied' : `Copy all (${filteredProxies.length})`}
                                  </button>
                                  {proxyPageCount > 1 && (
                                    <div className="flex items-center gap-1 ml-auto">
                                      <button
                                        onClick={() => setProxyPage((v) => Math.max(0, v - 1))}
                                        disabled={proxyPage === 0}
                                        className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                                      >
                                        Prev
                                      </button>
                                      <span className="text-xs text-gray-500">{proxyPage + 1} / {proxyPageCount}</span>
                                      <button
                                        onClick={() => setProxyPage((v) => Math.min(proxyPageCount - 1, v + 1))}
                                        disabled={proxyPage >= proxyPageCount - 1}
                                        className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                                      >
                                        Next
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {proxyPageList.length === 0 ? (
                                  <div className="text-sm text-gray-500 dark:text-gray-400 py-2">No proxies match.</div>
                                ) : (
                                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 max-h-72 overflow-y-auto pr-1">
                                    {proxyPageList.map((hp) => (
                                      <div
                                        key={hp}
                                        title={hp}
                                        className="px-2 py-1 text-xs font-mono text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 truncate"
                                      >
                                        {hp}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 dark:text-gray-400 py-2">Failed to load.</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BartProxies;
